import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StickyNote, Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "./dateFmt";

/* Order Notes — internal, append-only notes visible to every staff member on
 * a CLOSED order page only. Never printed, never emailed, never attached to
 * the guest profile. Notes cannot be edited or deleted once saved. */
export default function OrderNotesCard({ orderId, refreshKey }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await staffApi.get(`/orders/${orderId}/notes`);
      setNotes(r.data?.notes || []);
    } catch { setNotes([]); }
  };
  useEffect(() => { if (orderId) load(); /* eslint-disable-next-line */ }, [orderId, refreshKey]);

  const addNote = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await staffApi.post(`/orders/${orderId}/notes`, { text: body });
      setText("");
      await load();
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-4 border-slate-200" data-testid="order-notes-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold tracking-wide text-slate-800 uppercase">Order Notes</h3>
          </div>
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Lock className="h-3 w-3" /> Internal only · permanent
          </span>
        </div>

        <div className="space-y-2">
          <Textarea
            data-testid="order-note-input"
            rows={3}
            value={text}
            maxLength={2000}
            placeholder="Type a note for the team about this closed order…"
            onChange={(e) => setText(e.target.value)}
            className="resize-y"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              Visible to all staff. Shows who wrote it and when. Cannot be edited or deleted, and never appears on the guest's invoice or receipt.
            </p>
            <Button
              data-testid="order-note-add-btn"
              size="sm"
              disabled={busy || !text.trim()}
              onClick={addNote}
            >
              {busy ? "Adding…" : "Add Note"}
            </Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="order-notes-list">
          {notes.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="order-notes-empty">No notes on this order yet.</p>
          ) : notes.map((n) => (
            <div key={n.id} className="rounded-md border border-slate-200 bg-slate-50/70 p-3" data-testid={`order-note-${n.id}`}>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
              <p className="mt-2 text-xs text-slate-500">
                {n.by_name} · {fmtDateTime(n.created_at)}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
